import { api, type Item } from "../api";
import { createSwipeRow, type SwipeActionDef, type SwipeRowActions } from "../components/swipeRow";
import { showUndoToast } from "../components/toast";
import { msg } from "../messages";
import { isNavigationStale } from "../navigation";
import type { GroceryItemView } from "../types/dashboard";
import { closeAnimatedSheet, mountAnimatedSheet } from "../utils/sheetAnimation";
import { createIcon } from "../utils/icons";

const ROW_STAGGER_BASE = 3;
const ROW_STAGGER_CAP = 12;
const GROCERY_MOTION_MS = 400;
const GROCERY_FLIP_MS = 400;
const GROCERY_SECTION_MS = 420;
const GROCERY_EASE = "cubic-bezier(0.25, 1, 0.5, 1)";

type GrocerySectionKey = "to-buy" | "stocked";

interface GroceryRemovalAnchor {
    section: GrocerySectionKey;
    beforeId: number | null;
}

const groceryState = new WeakMap<HTMLElement, { items: GroceryItemView[]; inventory: Item[] }>();

let isGroceryAnimating = false;

function setGroceryState(
    container: HTMLElement,
    items: GroceryItemView[],
    inventory: Item[],
): void {
    groceryState.set(container, { items, inventory });
}

function getGroceryState(
    container: HTMLElement,
): { items: GroceryItemView[]; inventory: Item[] } | null {
    return groceryState.get(container) ?? null;
}

function getGroceryPage(container: HTMLElement): HTMLElement | null {
    return container.querySelector(".grocery-page");
}

function findGrocerySwipeRow(container: HTMLElement, itemId: number): HTMLElement | null {
    return container.querySelector(`.grocery-page [data-grocery-id="${itemId}"]`);
}

function sectionKeyFromHeading(heading: string): GrocerySectionKey {
    return heading === "TO BUY" ? "to-buy" : "stocked";
}

function headingFromSectionKey(key: GrocerySectionKey): string {
    return key === "to-buy" ? "TO BUY" : "STOCKED";
}

function itemsForSection(items: GroceryItemView[], key: GrocerySectionKey): GroceryItemView[] {
    return items.filter((i) => (key === "to-buy" ? i.active : !i.active));
}

function updateGroceryMasthead(container: HTMLElement, items: GroceryItemView[]): void {
    const status = container.querySelector<HTMLElement>(".grocery-status");
    if (status) {
        status.textContent = formatGroceryStatus(items.filter((i) => i.active).length);
    }
}

async function removeGroceryEmptyState(page: HTMLElement): Promise<void> {
    const empty = page.querySelector<HTMLElement>(".grocery-empty");
    if (!empty) return;
    empty.classList.add("grocery-empty--exiting");
    await waitAnimation(empty, GROCERY_SECTION_MS);
    empty.remove();
}

async function showGroceryEmptyState(
    page: HTMLElement,
    container: HTMLElement,
    inventory: Item[],
): Promise<void> {
    if (page.querySelector(".grocery-empty")) return;
    page.querySelectorAll(".grocery-section").forEach((s) => s.remove());
    const empty = document.createElement("div");
    empty.className = "empty-state grocery-empty grocery-empty--entering";

    const message = document.createElement("p");
    message.textContent = msg("grocery/empty");

    const actions = document.createElement("div");
    actions.className = "empty-actions";

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "btn btn-primary btn-block";
    addBtn.setAttribute("aria-label", "Add item");
    addBtn.append(createIcon("add", "icon-sm"), " Add item");
    addBtn.addEventListener("click", () => openAddSheet(inventory, container));

    actions.appendChild(addBtn);
    empty.appendChild(message);
    empty.appendChild(actions);
    page.appendChild(empty);
    await waitAnimation(empty, GROCERY_MOTION_MS);
}

const GROCERY_FLIP_CLASS = "grocery-flip-motion";

function resetRowMotion(row: HTMLElement): void {
    row.getAnimations().forEach((anim) => anim.cancel());
    row.classList.remove("cascade-glide");
    row.classList.remove(GROCERY_FLIP_CLASS);
    row.style.removeProperty("transform");
    row.style.removeProperty("transition");
}

function collectFollowingPageBlocks(section: HTMLElement): HTMLElement[] {
    const page = section.closest<HTMLElement>(".grocery-page");
    if (!page) return [];

    const blocks: HTMLElement[] = [];
    let pastSection = false;
    for (const child of [...page.children]) {
        if (child === section) {
            pastSection = true;
            continue;
        }
        if (
            pastSection &&
            (child.classList.contains("grocery-section") ||
                child.classList.contains("grocery-empty"))
        ) {
            blocks.push(child as HTMLElement);
        }
    }
    return blocks;
}

function collectFlipTargetsAfterRow(listEl: HTMLElement, rowEl: HTMLElement): HTMLElement[] {
    const inList = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")].filter(
        (row) => row !== rowEl,
    );
    const section = listEl.closest<HTMLElement>(".grocery-section");
    const following = section ? collectFollowingPageBlocks(section) : [];
    return [...inList, ...following];
}

function collectFlipTargetsBeforeInsert(listEl: HTMLElement): HTMLElement[] {
    const inList = [...listEl.querySelectorAll<HTMLElement>(".swipe-row")];
    const section = listEl.closest<HTMLElement>(".grocery-section");
    const following = section ? collectFollowingPageBlocks(section) : [];
    return [...inList, ...following];
}

function getFlexGap(page: HTMLElement): number {
    const styles = getComputedStyle(page);
    const gap = parseFloat(styles.rowGap || styles.gap || "0");
    return Number.isFinite(gap) ? gap : 24;
}

interface FlipRun {
    movers: HTMLElement[];
    deltas: Map<HTMLElement, number>;
    animations: Animation[];
    finished: Promise<HTMLElement[]>;
}

function startFlip(
    siblings: HTMLElement[],
    topsBefore: Map<HTMLElement, number>,
    durationMs = GROCERY_FLIP_MS,
): FlipRun {
    const movers: HTMLElement[] = [];
    const deltas = new Map<HTMLElement, number>();
    const animations: Animation[] = [];

    for (const row of siblings) {
        resetRowMotion(row);

        const delta = topsBefore.get(row)! - row.getBoundingClientRect().top;
        if (Math.abs(delta) < 0.5) continue;

        deltas.set(row, delta);
        row.classList.add(GROCERY_FLIP_CLASS);
        movers.push(row);

        animations.push(
            row.animate([{ transform: `translateY(${delta}px)` }, { transform: "translateY(0)" }], {
                duration: durationMs,
                easing: GROCERY_EASE,
                fill: "forwards",
            }),
        );
    }

    const finished =
        animations.length === 0
            ? Promise.resolve(movers)
            : Promise.all(animations.map((anim) => anim.finished.catch(() => undefined))).then(
                  () => movers,
              );

    return { movers, deltas, animations, finished };
}

function clearFlip(movers: HTMLElement[]): void {
    for (const row of movers) {
        resetRowMotion(row);
    }
}

type GroceryRowExitMode = "fade" | "to-stocked" | "to-buy";

function prepareRowForExit(rowEl: HTMLElement): void {
    const content = rowEl.querySelector<HTMLElement>(".swipe-row-content");
    if (content) {
        content.style.transform = "";
        content.classList.remove("is-dragging");
    }
}

function exitKeyframes(mode: GroceryRowExitMode): Keyframe[] {
    if (mode === "to-stocked" || mode === "to-buy") {
        return [
            { opacity: 1, transform: "translateX(0)" },
            { opacity: 0, transform: "translateX(-40px)" },
        ];
    }
    return [
        { opacity: 1, transform: "translateX(0)" },
        { opacity: 0, transform: "translateX(-12px)" },
    ];
}

function runExitAnimation(rowEl: HTMLElement, mode: GroceryRowExitMode): Animation {
    return rowEl.animate(exitKeyframes(mode), {
        duration: GROCERY_MOTION_MS,
        easing: GROCERY_EASE,
        fill: "forwards",
    });
}

interface RowPinSnapshot {
    top: number;
    height: number;
}

function snapshotRowPin(listEl: HTMLElement, rowEl: HTMLElement): RowPinSnapshot {
    const listRect = listEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    return {
        top: rowRect.top - listRect.top + listEl.scrollTop,
        height: rowRect.height,
    };
}

function pinRowOverlay(rowEl: HTMLElement, pin: RowPinSnapshot): void {
    rowEl.classList.add("grocery-row--exiting-overlay");
    rowEl.style.top = `${pin.top}px`;
    rowEl.style.left = "0";
    rowEl.style.right = "0";
    rowEl.style.width = "100%";
    rowEl.style.height = `${pin.height}px`;
    rowEl.style.margin = "0";
}

function unpinRowOverlay(rowEl: HTMLElement): void {
    rowEl.classList.remove("grocery-row--exiting-overlay");
    rowEl.style.removeProperty("top");
    rowEl.style.removeProperty("left");
    rowEl.style.removeProperty("right");
    rowEl.style.removeProperty("width");
    rowEl.style.removeProperty("height");
    rowEl.style.removeProperty("margin");
}

interface RowExitFlipOptions {
    excludeFollowing?: boolean;
}

interface CancellableRowExit {
    finished: Promise<boolean>;
    cancel: () => Promise<void>;
}

function animateRowExitWithFlip(
    listEl: HTMLElement,
    rowEl: HTMLElement,
    exitMode: GroceryRowExitMode = "fade",
    options?: RowExitFlipOptions,
): CancellableRowExit {
    let cancelled = false;
    let completed = false;

    const flipTargets = options?.excludeFollowing
        ? [...listEl.querySelectorAll<HTMLElement>(".swipe-row")].filter((row) => row !== rowEl)
        : collectFlipTargetsAfterRow(listEl, rowEl);
    const topsBefore = new Map(flipTargets.map((el) => [el, el.getBoundingClientRect().top]));
    const pin = snapshotRowPin(listEl, rowEl);

    pinRowOverlay(rowEl, pin);
    void listEl.offsetHeight;

    resetRowMotion(rowEl);
    prepareRowForExit(rowEl);

    const inactiveRow = rowEl.querySelector<HTMLElement>(".grocery-row--inactive");
    if (inactiveRow) inactiveRow.style.opacity = "1";

    const exitAnim = runExitAnimation(rowEl, exitMode);
    const flip = startFlip(flipTargets, topsBefore, GROCERY_MOTION_MS);

    const finished = Promise.all([flip.finished, exitAnim.finished.catch(() => undefined)]).then(
        () => {
            if (cancelled) return false;
            completed = true;
            rowEl.remove();
            clearFlip(flip.movers);
            return true;
        },
    );

    const cancel = async (): Promise<void> => {
        if (cancelled || completed) return;
        cancelled = true;

        exitAnim.cancel();
        flip.animations.forEach((anim) => anim.cancel());

        const rowStyle = getComputedStyle(rowEl);
        const rowReverse = rowEl.animate(
            [
                {
                    opacity: rowStyle.opacity,
                    transform:
                        rowStyle.transform === "none" ? "translateX(-12px)" : rowStyle.transform,
                },
                { opacity: "1", transform: "translateX(0)" },
            ],
            { duration: GROCERY_MOTION_MS, easing: GROCERY_EASE, fill: "forwards" },
        );

        const siblingReverses = flip.movers.map((row) => {
            const delta = flip.deltas.get(row)!;
            const style = getComputedStyle(row);
            const fromTransform = style.transform === "none" ? "translateY(0)" : style.transform;
            return row.animate(
                [{ transform: fromTransform }, { transform: `translateY(${delta}px)` }],
                { duration: GROCERY_MOTION_MS, easing: GROCERY_EASE, fill: "forwards" },
            );
        });

        await Promise.all([
            rowReverse.finished.catch(() => undefined),
            ...siblingReverses.map((anim) => anim.finished.catch(() => undefined)),
        ]);

        rowEl.getAnimations().forEach((anim) => anim.cancel());
        for (const row of flip.movers) {
            row.getAnimations().forEach((anim) => anim.cancel());
        }

        unpinRowOverlay(rowEl);
        clearFlip(flip.movers);
        resetRowMotion(rowEl);
    };

    return { finished, cancel };
}

async function collapseSectionIfEmpty(
    section: HTMLElement | null,
    listEl?: HTMLElement | null,
): Promise<void> {
    if (!section) return;
    const list = section.querySelector(".grocery-list");
    if (!list || list.children.length > 0) return;

    const page = section.closest<HTMLElement>(".grocery-page");
    if (!page) {
        listEl?.style.removeProperty("minHeight");
        section.remove();
        return;
    }

    const following = collectFollowingPageBlocks(section);

    const collapseHeight = section.offsetHeight;
    const shift = -(collapseHeight + getFlexGap(page));

    section.style.overflow = "hidden";

    const sectionAnim = section.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: GROCERY_SECTION_MS,
        easing: GROCERY_EASE,
        fill: "forwards",
    });

    const blockAnims = following.map((block) =>
        block.animate([{ transform: "translateY(0)" }, { transform: `translateY(${shift}px)` }], {
            duration: GROCERY_SECTION_MS,
            easing: GROCERY_EASE,
            fill: "forwards",
        }),
    );

    await Promise.all([
        sectionAnim.finished.catch(() => undefined),
        ...blockAnims.map((anim) => anim.finished.catch(() => undefined)),
    ]);

    listEl?.style.removeProperty("minHeight");
    section.remove();
    for (const block of following) {
        resetRowMotion(block);
    }
}

async function fadeInGroceryRow(rowEl: HTMLElement): Promise<void> {
    const anim = rowEl.animate(
        [
            { opacity: 0, transform: "translateX(-40px)" },
            { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: GROCERY_MOTION_MS, easing: GROCERY_EASE },
    );
    await anim.finished.catch(() => undefined);
    rowEl.style.removeProperty("opacity");
}

async function insertGroceryRowAnimated(
    listEl: HTMLElement,
    rowEl: HTMLElement,
    beforeEl?: HTMLElement | null,
): Promise<void> {
    const flipTargets = collectFlipTargetsBeforeInsert(listEl);
    const topsBefore = new Map(flipTargets.map((el) => [el, el.getBoundingClientRect().top]));

    rowEl.style.opacity = "0";
    if (beforeEl) {
        listEl.insertBefore(rowEl, beforeEl);
    } else {
        listEl.appendChild(rowEl);
    }

    const flip = startFlip(flipTargets, topsBefore, GROCERY_MOTION_MS);
    const rowEnter = rowEl.animate(
        [
            { opacity: 0, transform: "translateX(-40px)" },
            { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: GROCERY_MOTION_MS, easing: GROCERY_EASE },
    );

    await Promise.all([flip.finished, rowEnter.finished.catch(() => undefined)]);

    rowEl.style.removeProperty("opacity");
    clearFlip(flip.movers);
}

async function revealGrocerySection(
    section: HTMLElement,
    topsBefore?: Map<HTMLElement, number>,
): Promise<void> {
    const page = section.closest<HTMLElement>(".grocery-page");
    const following = page ? collectFollowingPageBlocks(section) : [];

    section.style.overflow = "hidden";
    section.style.height = "0";
    section.style.opacity = "0";
    void section.offsetHeight;

    const targetHeight = section.scrollHeight;
    const expandAnim = section.animate(
        [
            { height: "0px", opacity: 0 },
            { height: `${targetHeight}px`, opacity: 1 },
        ],
        { duration: GROCERY_SECTION_MS, easing: GROCERY_EASE, fill: "forwards" },
    );

    const blockAnims = following.map((block) => {
        const beforeTop = topsBefore?.get(block);
        const insertOffset =
            beforeTop !== undefined ? beforeTop - block.getBoundingClientRect().top : 0;
        return block.animate(
            [{ transform: `translateY(${insertOffset}px)` }, { transform: "translateY(0)" }],
            { duration: GROCERY_SECTION_MS, easing: GROCERY_EASE, fill: "forwards" },
        );
    });

    await Promise.all([
        expandAnim.finished.catch(() => undefined),
        ...blockAnims.map((anim) => anim.finished.catch(() => undefined)),
    ]);

    section.style.removeProperty("height");
    section.style.removeProperty("overflow");
    section.style.removeProperty("opacity");
    for (const block of following) {
        resetRowMotion(block);
    }
}

function getRemovalAnchor(rowEl: HTMLElement): GroceryRemovalAnchor {
    const section = rowEl.closest<HTMLElement>("[data-section]")!;
    const next = rowEl.nextElementSibling as HTMLElement | null;
    return {
        section: section.dataset.section as GrocerySectionKey,
        beforeId: next ? Number(next.dataset.groceryId) : null,
    };
}

async function ensureGrocerySection(
    page: HTMLElement,
    key: GrocerySectionKey,
    options?: { pendingRow?: HTMLElement },
): Promise<{ section: HTMLElement; created: boolean }> {
    const existing = page.querySelector<HTMLElement>(`[data-section="${key}"]`);
    if (existing) return { section: existing, created: false };

    await removeGroceryEmptyState(page);

    const heading = headingFromSectionKey(key);
    const section = document.createElement("section");
    section.className = "grocery-section";
    section.dataset.section = key;

    const h = document.createElement("h2");
    h.className = "grocery-section-title";
    h.textContent = heading;

    const list = document.createElement("div");
    list.className = "grocery-list";

    section.appendChild(h);
    section.appendChild(list);

    if (options?.pendingRow) {
        options.pendingRow.style.opacity = "0";
        list.appendChild(options.pendingRow);
    }

    section.style.overflow = "hidden";
    section.style.height = "0";
    section.style.opacity = "0";

    const stocked = page.querySelector<HTMLElement>('[data-section="stocked"]');
    const topsBefore = stocked
        ? new Map([[stocked, stocked.getBoundingClientRect().top]])
        : undefined;

    if (key === "to-buy") {
        const masthead = page.querySelector(".grocery-masthead");
        if (stocked) {
            page.insertBefore(section, stocked);
        } else if (masthead) {
            masthead.insertAdjacentElement("afterend", section);
        } else {
            page.prepend(section);
        }
    } else {
        page.appendChild(section);
    }

    const rowFade = options?.pendingRow ? fadeInGroceryRow(options.pendingRow) : Promise.resolve();
    await Promise.all([revealGrocerySection(section, topsBefore), rowFade]);
    return { section, created: true };
}

async function removeGroceryRowAnimated(
    container: HTMLElement,
    itemId: number,
    items: GroceryItemView[],
    inventory: Item[],
): Promise<boolean> {
    const page = getGroceryPage(container);
    const rowEl = findGrocerySwipeRow(container, itemId);
    if (!page || !rowEl) {
        renderGroceryContent(container, items, inventory);
        return false;
    }

    const section = rowEl.closest<HTMLElement>("[data-section]");
    const listEl = section?.querySelector<HTMLElement>(".grocery-list");

    let exitCompleted = true;
    if (listEl) {
        const exit = animateRowExitWithFlip(listEl, rowEl, "fade");
        exitCompleted = await exit.finished;
    }

    if (!exitCompleted) {
        return false;
    }

    setGroceryState(container, items, inventory);
    updateGroceryMasthead(container, items);
    return true;
}

async function restoreGroceryRowAnimated(
    container: HTMLElement,
    item: GroceryItemView,
    items: GroceryItemView[],
    inventory: Item[],
    anchor: GroceryRemovalAnchor,
): Promise<void> {
    const page = getGroceryPage(container);
    if (!page) {
        renderGroceryContent(container, items, inventory);
        return;
    }

    isGroceryAnimating = true;
    try {
        const rowEl = renderGroceryRow(item, items, container, inventory, anchor.section === "stocked");
        const sectionExists = page.querySelector(`[data-section="${anchor.section}"]`);

        if (!sectionExists) {
            await ensureGrocerySection(page, anchor.section, { pendingRow: rowEl });
        } else {
            const { section } = await ensureGrocerySection(page, anchor.section);
            const listEl = section.querySelector<HTMLElement>(".grocery-list")!;
            const beforeEl =
                anchor.beforeId !== null
                    ? listEl.querySelector<HTMLElement>(`[data-grocery-id="${anchor.beforeId}"]`)
                    : null;
            await insertGroceryRowAnimated(listEl, rowEl, beforeEl);
        }

        setGroceryState(container, items, inventory);
        updateGroceryMasthead(container, items);
    } finally {
        isGroceryAnimating = false;
    }
}

async function transferGroceryRowAnimated(
    container: HTMLElement,
    itemId: number,
    target: GrocerySectionKey,
    items: GroceryItemView[],
    inventory: Item[],
): Promise<void> {
    const page = getGroceryPage(container);
    const item = items.find((i) => i.id === itemId);
    const rowEl = findGrocerySwipeRow(container, itemId);
    if (!page || !item || !rowEl) {
        renderGroceryContent(container, items, inventory);
        return;
    }

    const sourceSection = rowEl.closest<HTMLElement>("[data-section]");
    const sourceList = sourceSection?.querySelector<HTMLElement>(".grocery-list");

    if (sourceList) {
        const exit = animateRowExitWithFlip(
            sourceList,
            rowEl,
            target === "stocked" ? "to-stocked" : "to-buy",
        );
        const exitCompleted = await exit.finished;
        if (!exitCompleted) {
            return;
        }
    }

    const newRow = renderGroceryRow(item, items, container, inventory, target === "stocked");
    const targetExists = page.querySelector(`[data-section="${target}"]`);

    if (!targetExists) {
        await ensureGrocerySection(page, target, { pendingRow: newRow });
    } else {
        const { section: targetSection } = await ensureGrocerySection(page, target);
        const targetList = targetSection.querySelector<HTMLElement>(".grocery-list")!;
        await insertGroceryRowAnimated(targetList, newRow);
    }

    setGroceryState(container, items, inventory);
    updateGroceryMasthead(container, items);
}

async function applyGroceryListFromServer(
    container: HTMLElement,
    options?: { animateNewId?: number },
): Promise<void> {
    isGroceryAnimating = true;
    try {
        const prev = getGroceryState(container);
        const [items, inventory] = await Promise.all([api.grocery.list(), api.items.list()]);

        if (!getGroceryPage(container) || !prev) {
            renderGroceryContent(container, items, inventory);
            return;
        }

        const prevIds = new Set(prev.items.map((i) => i.id));
        const newIds = new Set(items.map((i) => i.id));
        const added = items.filter((i) => !prevIds.has(i.id));
        const removed = prev.items.filter((i) => !newIds.has(i.id));
        const moved = items.filter((i) => {
            const p = prev.items.find((j) => j.id === i.id);
            return p !== undefined && p.active !== i.active;
        });

        if (options?.animateNewId && added.some((i) => i.id === options.animateNewId)) {
            const item = items.find((i) => i.id === options.animateNewId)!;
            const key: GrocerySectionKey = item.active ? "to-buy" : "stocked";
            setGroceryState(container, items, inventory);
            const page = getGroceryPage(container)!;
            await removeGroceryEmptyState(page);
            const rowEl = renderGroceryRow(item, items, container, inventory, key === "stocked");
            const sectionExists = page.querySelector(`[data-section="${key}"]`);

            if (!sectionExists) {
                await ensureGrocerySection(page, key, { pendingRow: rowEl });
            } else {
                const { section } = await ensureGrocerySection(page, key);
                const listEl = section.querySelector<HTMLElement>(".grocery-list")!;
                await insertGroceryRowAnimated(listEl, rowEl);
            }

            updateGroceryMasthead(container, items);
            return;
        }

        if (added.length === 1 && removed.length === 0 && moved.length === 0) {
            const item = added[0]!;
            const key: GrocerySectionKey = item.active ? "to-buy" : "stocked";
            setGroceryState(container, items, inventory);
            const page = getGroceryPage(container)!;
            await removeGroceryEmptyState(page);
            const rowEl = renderGroceryRow(item, items, container, inventory, key === "stocked");
            const sectionExists = page.querySelector(`[data-section="${key}"]`);

            if (!sectionExists) {
                await ensureGrocerySection(page, key, { pendingRow: rowEl });
            } else {
                const { section } = await ensureGrocerySection(page, key);
                const listEl = section.querySelector<HTMLElement>(".grocery-list")!;
                await insertGroceryRowAnimated(listEl, rowEl);
            }

            updateGroceryMasthead(container, items);
            return;
        }

        if (removed.length === 1 && added.length === 0 && moved.length === 0) {
            const item = removed[0]!;
            await removeGroceryRowAnimated(container, item.id, items, inventory);
            return;
        }

        if (moved.length === 1 && added.length === 0 && removed.length === 0) {
            const item = moved[0]!;
            const target: GrocerySectionKey = item.active ? "to-buy" : "stocked";
            await transferGroceryRowAnimated(container, item.id, target, items, inventory);
            return;
        }

        renderGroceryContent(container, items, inventory);
    } finally {
        isGroceryAnimating = false;
    }
}

function triggerViewEnter(page: HTMLElement): void {
    page.classList.remove("view-enter");
    void page.offsetWidth;
    page.classList.add("view-enter");
}

function formatGroceryStatus(activeCount: number): string {
    const text =
        activeCount === 0
            ? msg("grocery/subtitle-none")
            : activeCount === 1
              ? msg("grocery/subtitle-one")
              : msg("grocery/subtitle-many", { count: activeCount });
    return text.toUpperCase();
}

function renderGroceryLoadingShell(container: HTMLElement): void {
    container.innerHTML = "";

    const page = document.createElement("div");
    page.className = "grocery-page grocery-page--loading view-enter";

    const masthead = document.createElement("div");
    masthead.className = "grocery-masthead";

    const subBar = document.createElement("div");
    subBar.className = "grocery-shimmer-sub-bar loading-shimmer cascade-glide";
    subBar.style.setProperty("--stagger", "0");

    masthead.appendChild(subBar);

    const section = document.createElement("div");
    section.className = "grocery-shimmer-section loading-shimmer cascade-glide";
    section.style.setProperty("--stagger", "1");

    const rows = document.createElement("div");
    for (let i = 0; i < 6; i++) {
        const row = document.createElement("div");
        row.className = "grocery-shimmer-row loading-shimmer cascade-glide";
        row.style.setProperty("--stagger", String(2 + Math.min(i, 5)));
        rows.appendChild(row);
    }

    page.appendChild(masthead);
    page.appendChild(section);
    page.appendChild(rows);
    container.appendChild(page);
}

function waitAnimation(el: HTMLElement, fallbackMs: number): Promise<void> {
    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            el.removeEventListener("animationend", onEnd);
            resolve();
        };
        const onEnd = (e: AnimationEvent) => {
            if (e.target === el) finish();
        };
        el.addEventListener("animationend", onEnd);
        window.setTimeout(finish, fallbackMs);
    });
}

async function transitionToGroceryContent(
    container: HTMLElement,
    items: GroceryItemView[],
    inventory: Item[],
    gen: number,
): Promise<void> {
    const loadingPage = container.querySelector<HTMLElement>(".grocery-page--loading");
    if (loadingPage) {
        loadingPage.classList.add("grocery-page--exiting");
        await waitAnimation(loadingPage, 340);
    }
    if (isNavigationStale(gen)) return;
    renderGroceryContent(container, items, inventory, { animateEntrance: true });
}

export async function renderGrocery(container: HTMLElement, gen: number): Promise<void> {
    if (isNavigationStale(gen)) return;
    renderGroceryLoadingShell(container);

    window.addEventListener("pantry-sync", () => {
        if (isNavigationStale(gen) || isGroceryAnimating) return;
        void refreshGrocery(container);
    });

    try {
        const [items, inventory] = await Promise.all([api.grocery.list(), api.items.list()]);
        if (isNavigationStale(gen)) return;
        await transitionToGroceryContent(container, items, inventory, gen);
    } catch {
        if (isNavigationStale(gen)) return;
        container.innerHTML = `
      <div class="empty-state grocery-empty cascade-glide">
        <p>${msg("grocery/load-error")}</p>
      </div>
    `;
    }
}

function renderGroceryContent(
    container: HTMLElement,
    items: GroceryItemView[],
    inventory: Item[],
    options?: { animateEntrance?: boolean },
): void {
    const animateEntrance = options?.animateEntrance ?? false;

    container.innerHTML = "";
    const page = document.createElement("div");
    page.className = "grocery-page";

    const masthead = document.createElement("div");
    masthead.className = animateEntrance ? "grocery-masthead cascade-fade" : "grocery-masthead";

    const subBar = document.createElement("div");
    subBar.className = "grocery-sub-bar";

    const status = document.createElement("span");
    status.className = "grocery-status";
    status.textContent = formatGroceryStatus(items.filter((i) => i.active).length);

    const addBtn = document.createElement("button");
    addBtn.type = "button";
    addBtn.className = "grocery-add-btn";
    addBtn.setAttribute("aria-label", "Add to list");
    addBtn.textContent = "+";
    addBtn.addEventListener("click", () => openAddSheet(inventory, container));

    subBar.appendChild(status);
    subBar.appendChild(addBtn);
    masthead.appendChild(subBar);
    page.appendChild(masthead);

    const active = items.filter((i) => i.active);
    const inactive = items.filter((i) => !i.active);

    page.appendChild(
        renderGrocerySection(
            "TO BUY",
            active,
            items,
            container,
            inventory,
            1,
            animateEntrance,
        ),
    );
    page.appendChild(
        renderGrocerySection(
            "STOCKED",
            inactive,
            items,
            container,
            inventory,
            2,
            animateEntrance,
        ),
    );

    container.appendChild(page);
    setGroceryState(container, items, inventory);
    if (animateEntrance) triggerViewEnter(page);
}

function renderGrocerySection(
    heading: string,
    sectionItems: GroceryItemView[],
    allItems: GroceryItemView[],
    container: HTMLElement,
    inventory: Item[],
    sectionStagger: number,
    animateEntrance = false,
): HTMLElement {
    const section = document.createElement("section");
    section.className = animateEntrance ? "grocery-section cascade-glide" : "grocery-section";
    section.dataset.section = sectionKeyFromHeading(heading);
    if (animateEntrance) section.style.setProperty("--stagger", String(sectionStagger));

    const h = document.createElement("h2");
    h.className = "grocery-section-title";
    h.textContent = heading;
    section.appendChild(h);

    const list = document.createElement("div");
    list.className = "grocery-list";
    for (let i = 0; i < sectionItems.length; i++) {
        const rowStagger = animateEntrance
            ? sectionStagger === 1
                ? ROW_STAGGER_BASE + Math.min(i, ROW_STAGGER_CAP)
                : undefined
            : undefined;
        const swipeRow = renderGroceryRow(
            sectionItems[i],
            allItems,
            container,
            inventory,
            heading === "STOCKED",
            rowStagger,
        );
        list.appendChild(swipeRow);
    }
    section.appendChild(list);
    return section;
}

function appendGroceryMeta(meta: HTMLElement, item: GroceryItemView): void {
    if (item.mode === "manual") {
        meta.textContent = "Manual";
        return;
    }

    const qty = item.item_quantity ?? 0;
    const min = item.min_quantity ?? 1;
    const stocked = qty >= min;

    meta.appendChild(document.createTextNode("Auto · "));

    if (stocked) {
        meta.appendChild(document.createTextNode(`stocked (${qty} ≥ ${min})`));
        return;
    }

    const stockSpan = document.createElement("span");
    stockSpan.className = "grocery-meta-stock--low";
    stockSpan.textContent = `${qty} in stock`;
    meta.appendChild(stockSpan);
    meta.appendChild(document.createTextNode(` (need ≥ ${min})`));
}

function renderGroceryRow(
    item: GroceryItemView,
    allItems: GroceryItemView[],
    container: HTMLElement,
    inventory: Item[],
    isStockedSection: boolean,
    rowStagger?: number,
): HTMLElement {
    const row = document.createElement("div");
    row.className = `grocery-row${item.active ? "" : " grocery-row--inactive"}`;

    const info = document.createElement("div");
    info.className = "grocery-row-info";

    const name = document.createElement("span");
    name.className = "grocery-name";
    name.textContent = item.mode === "auto" ? (item.item_name ?? item.name) : item.name;

    const meta = document.createElement("span");
    meta.className = "grocery-meta";
    appendGroceryMeta(meta, item);

    info.appendChild(name);
    info.appendChild(meta);
    row.appendChild(info);

    const rightAction: SwipeActionDef =
        item.mode === "auto"
            ? {
                  label: "Edit",
                  className: "swipe-action-edit",
                  onAction: () => openGroceryEditSheet(item, allItems, container, inventory),
              }
            : {
                  label: "Toss",
                  className: "swipe-action-toss",
                  onAction: () => void beginDeferredGroceryRemoval(item, allItems, container, inventory),
              };

    const actions: SwipeRowActions = isStockedSection
        ? {
              left: {
                  label: "Flag",
                  className: "swipe-action-flag",
                  onAction: () => moveGroceryToBuy(item, container),
              },
              right: rightAction,
          }
        : {
              left: {
                  label: "Restock",
                  className: "swipe-action-restock",
                  onAction: () => stockGroceryItem(item, container),
              },
              right: rightAction,
          };

    const swipeRow = createSwipeRow(row, actions);

    swipeRow.dataset.groceryId = String(item.id);

    if (rowStagger !== undefined) {
        swipeRow.classList.add("cascade-glide");
        swipeRow.style.setProperty("--stagger", String(rowStagger));
    }

    return swipeRow;
}

async function stockGroceryItem(item: GroceryItemView, container: HTMLElement): Promise<void> {
    isGroceryAnimating = true;
    try {
        if (item.mode === "manual") {
            await api.grocery.update(item.id, { stocked: true });
        } else if (item.item_id) {
            const minQty = item.min_quantity ?? 1;
            await api.items.update(item.item_id, { quantity: minQty });
        } else {
            return;
        }
        const [items, inventory] = await Promise.all([api.grocery.list(), api.items.list()]);
        await transferGroceryRowAnimated(container, item.id, "stocked", items, inventory);
    } catch {
        /* silent failure */
    } finally {
        isGroceryAnimating = false;
    }
}

async function moveGroceryToBuy(item: GroceryItemView, container: HTMLElement): Promise<void> {
    if (item.mode === "manual") {
        isGroceryAnimating = true;
        try {
            await api.grocery.update(item.id, { stocked: false });
            const [items, inventory] = await Promise.all([api.grocery.list(), api.items.list()]);
            await transferGroceryRowAnimated(container, item.id, "to-buy", items, inventory);
        } catch {
            /* silent failure */
        } finally {
            isGroceryAnimating = false;
        }
        return;
    }
    openAutoRestockPrompt(item, container);
}

function openAutoRestockPrompt(item: GroceryItemView, container: HTMLElement): void {
    const qty = item.item_quantity ?? 0;
    const min = item.min_quantity ?? 1;
    const lowerQty = Math.max(0, min - 1);

    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";

    const sheet = document.createElement("div");
    sheet.className = "item-sheet";

    const header = document.createElement("header");
    header.className = "item-sheet-header";

    const heading = document.createElement("h2");
    heading.className = "item-sheet-title";
    heading.textContent = msg("grocery/restock-title", { name: item.name });

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "item-sheet-dismiss";
    dismissBtn.setAttribute("aria-label", "Close");
    dismissBtn.textContent = "×";

    header.appendChild(heading);
    header.appendChild(dismissBtn);

    const body = document.createElement("div");
    body.className = "item-sheet-body";

    const raisedMin = qty + 1;

    const lowerBtn = document.createElement("button");
    lowerBtn.type = "button";
    lowerBtn.className = "grocery-restock-option";
    lowerBtn.innerHTML = `
      <span class="grocery-restock-option-label">${msg("grocery/restock-lower", { qty: lowerQty })}</span>
      <span class="grocery-restock-option-hint">${msg("grocery/restock-lower-hint", { min })}</span>
    `;

    const raiseBtn = document.createElement("button");
    raiseBtn.type = "button";
    raiseBtn.className = "grocery-restock-option";
    raiseBtn.innerHTML = `
      <span class="grocery-restock-option-label">${msg("grocery/restock-raise", { min: raisedMin })}</span>
      <span class="grocery-restock-option-hint">${msg("grocery/restock-raise-hint", { qty })}</span>
    `;

    function close(): void {
        closeAnimatedSheet(overlay);
    }

    dismissBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
    });

    async function completeRestockMove(): Promise<void> {
        try {
            const [items, inventory] = await Promise.all([api.grocery.list(), api.items.list()]);
            await transferGroceryRowAnimated(container, item.id, "to-buy", items, inventory);
        } finally {
            isGroceryAnimating = false;
        }
    }

    lowerBtn.addEventListener("click", async () => {
        if (!item.item_id) return;
        try {
            isGroceryAnimating = true;
            await api.items.update(item.item_id, { quantity: lowerQty });
            closeAnimatedSheet(overlay, () => {
                void completeRestockMove();
            });
        } catch {
            isGroceryAnimating = false;
        }
    });

    raiseBtn.addEventListener("click", async () => {
        try {
            isGroceryAnimating = true;
            await api.grocery.update(item.id, { min_quantity: raisedMin });
            closeAnimatedSheet(overlay, () => {
                void completeRestockMove();
            });
        } catch {
            isGroceryAnimating = false;
        }
    });

    body.appendChild(lowerBtn);
    body.appendChild(raiseBtn);

    sheet.appendChild(header);
    sheet.appendChild(body);
    overlay.appendChild(sheet);

    mountAnimatedSheet(overlay);
}

function openGroceryEditSheet(
    item: GroceryItemView,
    allItems: GroceryItemView[],
    container: HTMLElement,
    inventory: Item[],
): void {
    const displayName = item.mode === "auto" ? (item.item_name ?? item.name) : item.name;

    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";

    const sheet = document.createElement("div");
    sheet.className = "item-sheet";

    const header = document.createElement("header");
    header.className = "item-sheet-header";

    const heading = document.createElement("h2");
    heading.className = "item-sheet-title";
    heading.textContent = `Edit: ${displayName}`;

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "item-sheet-dismiss";
    dismissBtn.setAttribute("aria-label", "Close");
    dismissBtn.textContent = "×";

    header.appendChild(heading);
    header.appendChild(dismissBtn);

    const body = document.createElement("div");
    body.className = "item-sheet-body";

    const minField = document.createElement("div");
    minField.className = "ledger-field";
    minField.innerHTML = `<label class="ledger-label" for="grocery-edit-min">Flag when below</label>`;
    const minInput = document.createElement("input");
    minInput.id = "grocery-edit-min";
    minInput.type = "number";
    minInput.className = "ledger-input";
    minInput.min = "1";
    minInput.step = "1";
    minInput.value = String(item.min_quantity ?? 1);
    minField.appendChild(minInput);

    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "item-sheet-submit";
    saveBtn.textContent = "SAVE";

    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "item-sheet-danger";
    deleteBtn.textContent = "Delete";

    function close(): void {
        closeAnimatedSheet(overlay);
    }

    dismissBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
    });

    saveBtn.addEventListener("click", async () => {
        const newMin = Number(minInput.value);
        if (!Number.isInteger(newMin) || newMin <= 0) return;

        try {
            await api.grocery.update(item.id, { min_quantity: newMin });
            closeAnimatedSheet(overlay, () => {
                void refreshGrocery(container);
            });
        } catch {
            /* silent failure */
        }
    });

    deleteBtn.addEventListener("click", () => {
        closeAnimatedSheet(overlay, () => {
            void beginDeferredGroceryRemoval(item, allItems, container, inventory);
        });
    });

    body.appendChild(minField);
    body.appendChild(saveBtn);
    body.appendChild(deleteBtn);

    sheet.appendChild(header);
    sheet.appendChild(body);
    overlay.appendChild(sheet);

    mountAnimatedSheet(overlay);
}

async function beginDeferredGroceryRemoval(
    item: GroceryItemView,
    allItems: GroceryItemView[],
    container: HTMLElement,
    inventory: Item[],
): Promise<void> {
    const rowEl = findGrocerySwipeRow(container, item.id);
    if (!rowEl) return;

    isGroceryAnimating = true;
    try {
        const anchor = getRemovalAnchor(rowEl);
        const remaining = allItems.filter((i) => i.id !== item.id);

        const removed = await removeGroceryRowAnimated(container, item.id, remaining, inventory);
        if (!removed) return;

        const removedName = item.mode === "auto" ? (item.item_name ?? item.name) : item.name;
        showUndoToast({
            message: msg("toast/removed", { name: removedName }),
            durationMs: 5000,
            onUndo: () => {
                void restoreGroceryRowAnimated(container, item, allItems, inventory, anchor);
            },
            onComplete: async () => {
                try {
                    await api.grocery.delete(item.id);
                } catch {
                    renderGroceryContent(container, allItems, inventory);
                }
            },
        });
    } finally {
        isGroceryAnimating = false;
    }
}

async function refreshGrocery(container: HTMLElement, createdId?: number): Promise<void> {
    await applyGroceryListFromServer(
        container,
        createdId ? { animateNewId: createdId } : undefined,
    );
}

function openAddSheet(inventory: Item[], container: HTMLElement): void {
    const overlay = document.createElement("div");
    overlay.className = "sheet-overlay";

    const sheet = document.createElement("div");
    sheet.className = "item-sheet";

    const header = document.createElement("header");
    header.className = "item-sheet-header";

    const heading = document.createElement("h2");
    heading.className = "item-sheet-title";
    heading.textContent = "Add to Grocery List";

    const dismissBtn = document.createElement("button");
    dismissBtn.type = "button";
    dismissBtn.className = "item-sheet-dismiss";
    dismissBtn.setAttribute("aria-label", "Close");
    dismissBtn.textContent = "×";

    header.appendChild(heading);
    header.appendChild(dismissBtn);

    const body = document.createElement("div");
    body.className = "item-sheet-body";

    let mode: "manual" | "auto" = "manual";

    const chips = document.createElement("div");
    chips.className = "filter-chips";

    const manualChip = document.createElement("button");
    manualChip.type = "button";
    manualChip.className = "chip active";
    manualChip.textContent = "Manual";

    const autoChip = document.createElement("button");
    autoChip.type = "button";
    autoChip.className = "chip";
    autoChip.textContent = "Auto Restock";

    chips.appendChild(manualChip);
    chips.appendChild(autoChip);

    const manualField = document.createElement("div");
    manualField.className = "ledger-field grocery-add-field is-active";
    manualField.innerHTML = `<label class="ledger-label" for="grocery-manual-name">Name</label>`;
    const nameInput = document.createElement("input");
    nameInput.id = "grocery-manual-name";
    nameInput.type = "text";
    nameInput.className = "ledger-input";
    nameInput.placeholder = "e.g. Whole milk";
    nameInput.autocomplete = "off";
    manualField.appendChild(nameInput);

    const autoFields = document.createElement("div");
    autoFields.className = "grocery-add-field";

    const itemField = document.createElement("div");
    itemField.className = "ledger-field";
    itemField.innerHTML = `<label class="ledger-label" for="grocery-auto-item">Inventory item</label>`;
    const itemSelect = document.createElement("select");
    itemSelect.id = "grocery-auto-item";
    itemSelect.className = "ledger-input";
    const emptyOpt = document.createElement("option");
    emptyOpt.value = "";
    emptyOpt.textContent = "Select item…";
    itemSelect.appendChild(emptyOpt);
    for (const inv of inventory) {
        const opt = document.createElement("option");
        opt.value = String(inv.id);
        opt.textContent = inv.name;
        itemSelect.appendChild(opt);
    }
    itemField.appendChild(itemSelect);

    const minField = document.createElement("div");
    minField.className = "ledger-field";
    minField.innerHTML = `<label class="ledger-label" for="grocery-auto-min">Restock when below</label>`;
    const minInput = document.createElement("input");
    minInput.id = "grocery-auto-min";
    minInput.type = "number";
    minInput.className = "ledger-input";
    minInput.min = "1";
    minInput.step = "1";
    minInput.value = "1";
    minField.appendChild(minInput);

    autoFields.appendChild(itemField);
    autoFields.appendChild(minField);

    const fields = document.createElement("div");
    fields.className = "grocery-add-fields";
    fields.appendChild(manualField);
    fields.appendChild(autoFields);

    function setMode(m: "manual" | "auto"): void {
        mode = m;
        manualChip.classList.toggle("active", m === "manual");
        autoChip.classList.toggle("active", m === "auto");
        manualField.classList.toggle("is-active", m === "manual");
        autoFields.classList.toggle("is-active", m === "auto");
    }

    manualChip.addEventListener("click", () => setMode("manual"));
    autoChip.addEventListener("click", () => setMode("auto"));

    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "item-sheet-submit";
    saveBtn.textContent = "ADD TO LIST";

    function close(): void {
        closeAnimatedSheet(overlay);
    }

    dismissBtn.addEventListener("click", close);
    overlay.addEventListener("click", (e) => {
        if (e.target === overlay) close();
    });

    saveBtn.addEventListener("click", async () => {
        try {
            let created;
            if (mode === "manual") {
                const name = nameInput.value.trim();
                if (!name) return;
                created = await api.grocery.create({ mode: "manual", name });
            } else {
                const itemId = Number(itemSelect.value);
                if (!itemId) return;
                created = await api.grocery.create({
                    mode: "auto",
                    item_id: itemId,
                    min_quantity: Number(minInput.value) || 1,
                });
            }
            closeAnimatedSheet(overlay, () => {
                void refreshGrocery(container, created.id);
            });
        } catch {
            /* silent failure */
        }
    });

    body.appendChild(chips);
    body.appendChild(fields);
    body.appendChild(saveBtn);

    sheet.appendChild(header);
    sheet.appendChild(body);
    overlay.appendChild(sheet);

    mountAnimatedSheet(overlay);
}
